import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { fetchCalls } from "../voiceApi";
import { buildSmsIndex } from "../buildSmsIndex";
import type { VoiceCall } from "../types";
import { extractDigits, formatPhone } from "../phone";
import {
  CALL_TAG_LABELS,
  CALL_TAG_STYLES,
  tagCalls,
  type CallTag,
  type SmsIndexEntry,
  type TaggedCall,
} from "../tagCall";

// Phase 4 §5c — reporting-only call log. Stays boring on purpose (see
// HQ_Dashboard_Plan.md §5c "Why this, not a richer CDR"). Triage lives in
// /voice (voicemail inbox); this tab exists to answer "how many calls went
// to VM last week on Line 2?" without needing a SQL editor.
//
// "Sent a Text" tagging is composite — it can co-occur with any primary
// tag. As of Phase 2.5 (April 22, 2026 post-Track-B) the cross-module
// SMS index is live: we fetch outbound messages in the same date window
// and pass them into `tagCalls` so the badge lights up on rows where
// staff texted the caller within ±60 min from the same line. See
// `../buildSmsIndex.ts` for the fail-closed fetch and ±60 min halo.

type Props = {
  phoneLine: "line1" | "line2" | null;
};

type TagFilter = "all" | CallTag;

const TAG_FILTER_OPTIONS: { value: TagFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "answered", label: CALL_TAG_LABELS.answered },
  { value: "ai_answered", label: CALL_TAG_LABELS.ai_answered },
  { value: "left_vm", label: CALL_TAG_LABELS.left_vm },
  { value: "missed", label: CALL_TAG_LABELS.missed },
  { value: "in_progress", label: CALL_TAG_LABELS.in_progress },
];

const PAGE_SIZE = 100;

// Today's local-date ISO stub (YYYY-MM-DD). Used as the default `to` value
// so the date picker opens on a humane range rather than Unix epoch math.
function localDateStub(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function toIsoStart(stub: string): string {
  // Interpret the <input type="date"> value as local midnight, convert to
  // UTC ISO. This matches how humans expect "from April 1" to behave.
  const d = new Date(`${stub}T00:00:00`);
  return d.toISOString();
}

function toIsoEndExclusive(stub: string): string {
  // Exclusive upper bound — add 24h so the user-typed "to April 22"
  // includes calls that happened on April 22.
  const d = new Date(`${stub}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString();
}

function csvEscape(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(tagged: TaggedCall[], from: string, to: string): void {
  const header = [
    "created_at",
    "caller_number",
    "phone_line",
    "outcome",
    "ui_tag",
    "sent_text",
    "recording_duration_seconds",
    "transcript",
  ];
  const lines = [header.join(",")];
  for (const { call, tag, sentText } of tagged) {
    lines.push(
      [
        call.created_at,
        call.caller_number,
        call.phone_line ?? "",
        call.outcome,
        CALL_TAG_LABELS[tag],
        sentText ? "yes" : "",
        call.recording_duration ?? "",
        call.transcript ?? "",
      ]
        .map(csvEscape)
        .join(",")
    );
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = `tribal-axe-calls-${from}_to_${to}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(href);
}

export function CallsView({ phoneLine }: Props) {
  // Default window = last 30 days ending today (inclusive). Matches the
  // worker's default when neither `from` nor `to` is sent.
  const today = useMemo(() => new Date(), []);
  const [fromDate, setFromDate] = useState<string>(() =>
    localDateStub(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
  );
  const [toDate, setToDate] = useState<string>(() => localDateStub(today));
  const [tagFilter, setTagFilter] = useState<TagFilter>("all");
  const [offset, setOffset] = useState(0);

  const [rows, setRows] = useState<VoiceCall[]>([]);
  const [smsIndex, setSmsIndex] = useState<SmsIndexEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const load = useCallback(
    async (nextOffset: number) => {
      setLoading(true);
      setLoadError(null);
      try {
        const fromIso = toIsoStart(fromDate);
        const toIso = toIsoEndExclusive(toDate);
        if (Date.parse(fromIso) > Date.parse(toIso)) {
          throw new Error("'From' date must be before 'To' date.");
        }
        // Calls + SMS index fetched in parallel. The index is scoped to
        // the same window (plus the ±60 min halo buildSmsIndex adds) so
        // boundary calls still get their "Sent a Text" badge. A failed
        // index fetch degrades gracefully — buildSmsIndex returns `[]`
        // on error, so the calls table still renders without the
        // composite badge rather than erroring the whole view.
        const [data, index] = await Promise.all([
          fetchCalls({
            from: fromIso,
            to: toIso,
            phoneLine,
            limit: PAGE_SIZE,
            offset: nextOffset,
          }),
          buildSmsIndex({ fromIso, toIso }),
        ]);
        setRows(data);
        setSmsIndex(index);
        // Offset-based "is there another page?" — if the worker returned a
        // full page, assume there's more and let the user click Next. A
        // short page means we're at the tail. Good enough for the small
        // row counts 5c is scoped for.
        setHasMore(data.length === PAGE_SIZE);
      } catch (err) {
        setLoadError((err as Error).message);
        setRows([]);
        setSmsIndex([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [fromDate, toDate, phoneLine]
  );

  // Whenever the inputs change, reset to page 0. Separate `offset` effect
  // handles pagination without re-resetting the page on every render.
  useEffect(() => {
    setOffset(0);
    load(0);
  }, [load]);

  useEffect(() => {
    if (offset !== 0) load(offset);
    // Intentionally omit `load` — we only want this effect to react to
    // offset changes, not to input-driven `load` recreation (that path
    // already runs via the effect above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  const tagged: TaggedCall[] = useMemo(() => tagCalls(rows, smsIndex), [rows, smsIndex]);
  const filtered = useMemo(
    () => (tagFilter === "all" ? tagged : tagged.filter((t) => t.tag === tagFilter)),
    [tagged, tagFilter]
  );

  const tagCounts = useMemo(() => {
    const acc: Record<TagFilter, number> = {
      all: tagged.length,
      answered: 0,
      ai_answered: 0,
      left_vm: 0,
      missed: 0,
      in_progress: 0,
      unknown: 0,
    };
    for (const t of tagged) acc[t.tag] += 1;
    return acc;
  }, [tagged]);

  function handleCopy(call: VoiceCall) {
    navigator.clipboard.writeText(extractDigits(call.caller_number)).catch(() => {
      // Silent — copy is a convenience, not a critical path.
    });
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 border-b border-tribal-border bg-tribal-surface px-4 py-3">
        <h2 className="text-base font-semibold">Calls</h2>

        <div className="flex items-center gap-2 text-xs text-tribal-muted">
          <label className="flex items-center gap-1">
            From
            <input
              type="date"
              value={fromDate}
              max={toDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-md border border-tribal-border bg-black/30 px-2 py-1 text-xs text-white focus:border-tribal-red focus:outline-none"
            />
          </label>
          <label className="flex items-center gap-1">
            To
            <input
              type="date"
              value={toDate}
              min={fromDate}
              max={localDateStub(today)}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-md border border-tribal-border bg-black/30 px-2 py-1 text-xs text-white focus:border-tribal-red focus:outline-none"
            />
          </label>
        </div>

        <div
          role="tablist"
          aria-label="Filter by disposition"
          className="flex items-center gap-0.5 rounded-lg bg-black/30 p-0.5"
        >
          {TAG_FILTER_OPTIONS.map((opt) => {
            const active = tagFilter === opt.value;
            const count = tagCounts[opt.value];
            return (
              <button
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTagFilter(opt.value)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  active
                    ? "bg-tribal-border text-white"
                    : "text-tribal-muted hover:text-white"
                }`}
              >
                {opt.label}
                {count > 0 && (
                  <span className="ml-1.5 rounded-full bg-white/10 px-1.5 py-0.5 text-[10px]">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {loadError && <span className="text-xs text-red-400">{loadError}</span>}
          <button
            type="button"
            onClick={() => downloadCsv(filtered, fromDate, toDate)}
            disabled={filtered.length === 0}
            className="rounded-md bg-white/5 px-3 py-1 text-xs text-tribal-muted transition-colors hover:bg-white/10 hover:text-white disabled:opacity-40"
            title="Download current view as CSV"
          >
            Export CSV
          </button>
          <button
            type="button"
            onClick={() => load(offset)}
            className="rounded-md bg-white/5 px-3 py-1 text-xs text-tribal-muted transition-colors hover:bg-white/10 hover:text-white"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="min-h-0 flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-sm text-tribal-muted">
            Loading calls…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center text-tribal-muted">
            <div className="mb-3 text-5xl" aria-hidden>
              📞
            </div>
            <p className="text-sm">
              {tagged.length === 0
                ? "No calls in this window."
                : "No calls match the current filter."}
            </p>
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-tribal-surface text-left text-xs uppercase tracking-wider text-tribal-muted">
              <tr>
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-4 py-2 font-medium">Caller</th>
                <th className="px-4 py-2 font-medium">Line</th>
                <th className="px-4 py-2 font-medium">Disposition</th>
                <th className="px-4 py-2 font-medium">Duration</th>
                <th className="px-4 py-2 font-medium">Transcript</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(({ call, tag, sentText }) => {
                const badge = CALL_TAG_STYLES[tag];
                return (
                  <tr
                    key={call.id}
                    className="border-t border-tribal-border/60 align-top hover:bg-white/[0.02]"
                  >
                    <td className="whitespace-nowrap px-4 py-2 text-tribal-muted">
                      {format(new Date(call.created_at), "MMM d, h:mm a")}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-white">{formatPhone(call.caller_number)}</span>
                        <button
                          type="button"
                          onClick={() => handleCopy(call)}
                          title="Copy number"
                          className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] text-tribal-muted transition-colors hover:bg-white/10 hover:text-white"
                        >
                          Copy
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-tribal-muted">
                      {call.phone_line ? call.phone_line.replace("line", "Line ") : "—"}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className={`rounded-full px-2 py-0.5 text-xs ${badge}`}>
                          {CALL_TAG_LABELS[tag]}
                        </span>
                        {sentText && (
                          <span
                            className="rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-medium text-white"
                            title="Staff followed up with an SMS within 60 minutes"
                          >
                            Sent a Text
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-tribal-muted">
                      {call.recording_duration && call.recording_duration > 0
                        ? `${call.recording_duration}s`
                        : "—"}
                    </td>
                    <td className="max-w-md px-4 py-2 text-tribal-muted">
                      {call.transcript ? (
                        <span className="line-clamp-2 italic">"{call.transcript}"</span>
                      ) : (
                        <span className="text-tribal-muted/40">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="flex flex-shrink-0 items-center justify-between border-t border-tribal-border bg-tribal-surface px-4 py-2 text-xs text-tribal-muted">
        <div>
          {tagged.length > 0 ? (
            <>
              Showing {filtered.length} of {tagged.length} on this page
              {tagFilter !== "all" ? ` (${CALL_TAG_LABELS[tagFilter as CallTag]} filter)` : ""}
              {" · "}
              page {Math.floor(offset / PAGE_SIZE) + 1}
            </>
          ) : (
            "No rows on this page"
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={offset === 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            className="rounded-md bg-white/5 px-3 py-1 text-xs text-tribal-muted transition-colors hover:bg-white/10 hover:text-white disabled:opacity-40"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={!hasMore || loading}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="rounded-md bg-white/5 px-3 py-1 text-xs text-tribal-muted transition-colors hover:bg-white/10 hover:text-white disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
